import { Link } from "react-router-dom";
import { Globe, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth.jsx";

/**
 * ProfileEditorEntryCard — Owner-facing entry point to the public
 * `/t/:slug` profile editor.
 *
 * Two states:
 *   - Owner / admin: green "Manage public profile" CTA → /dashboard/profile-editor.
 *     Free owners can edit logo/cover/description and publish too; the
 *     custom logo MAP PIN remains a Pro-only perk and is gated inside
 *     the editor itself.
 *   - Anyone else (customer, logged out): hidden (returns null)
 */
export default function ProfileEditorEntryCard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== "owner") return null;
  const isPro = !!(user.is_pro || user.is_admin);

  return (
    <Link
      to="/dashboard/profile-editor"
      className="block rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:bg-primary/[0.02] transition"
      data-testid="profile-editor-entry"
    >
      <div className="flex items-start gap-3">
        <div className="flex-none h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Globe weight="duotone" size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Public profile
          </div>
          <h3 className="font-display font-bold text-lg leading-snug mt-0.5">
            Manage your <code className="font-mono text-base">/t/your-slug</code> page
          </h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Edit your logo, cover, menu, and links — then publish to make the page visible to customers and
            shareable across your bio + social.
          </p>
          {!isPro && (
            <p
              className="text-[11px] text-amber-700 dark:text-amber-300 mt-1.5 leading-relaxed"
              data-testid="profile-editor-pro-hint"
            >
              Free tier — your map pin stays the food-category icon.{" "}
              <span className="font-semibold">Upgrade to Pro</span> to use your logo on the map.
            </p>
          )}
        </div>
        <ArrowRight
          size={20}
          weight="bold"
          className="flex-none text-muted-foreground"
        />
      </div>
    </Link>
  );
}
